/* eslint-disable jsx-a11y/no-onchange */
/* eslint-disable react/no-did-update-set-state */

import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { omit } from 'lodash';

import Label from '../Label';
import SelectOption from './SelectOption';

export default class Select extends React.PureComponent {
    static propTypes = {
        /** html id required to connect the select with the label */
        id: PropTypes.string.isRequired,

        /** Label contents, usually text */
        label: PropTypes.string.isRequired,

        /** If false, the label will be screen reader only */
        isLabelVisible: PropTypes.bool,

        /** Marks the field as required; Adds required styling to label */
        isRequired: PropTypes.bool,

        /** Disables the select */
        isDisabled: PropTypes.bool,

        /** Makes the label and select arranged inline horizontally */
        isInline: PropTypes.bool,

        /** Adds an unselectable placeholder item */
        placeholder: PropTypes.string,

        /** Contents of the error message, usually text */
        errorText: PropTypes.node,

        /** Marks and styles the field as invalid */
        isInvalid: PropTypes.bool,

        /** The value of the current selected options */
        value: PropTypes.string,

        /** The initial value on render. The default is an empty string because
        placeholder's have a value of an empty string. */
        defaultValue: PropTypes.string,

        /** One or more `SelectOption`s */
        children: PropTypes.node,

        /** The ref to be passed to the input */
        innerRef: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),

        /** As an alternative to passing in children, you can specify an array
        of objects and an select option will be generated for each array item */
        options: PropTypes.arrayOf(PropTypes.shape({
            label: PropTypes.string,
            value: PropTypes.string
        })),

        /** additional class applied to outer container */
        className: PropTypes.string
    };

    static defaultProps = {
        isLabelVisible: true,
        isDisabled: false,
        isRequired: false,
        isInline: false,
        children: undefined,
        isInvalid: false,
        value: undefined,
        defaultValue: '',
        innerRef: undefined,
        className: undefined,
        errorText: undefined
    };

    constructor(props) {
        super(props);

        this.state = {
            isInvalid: this.props.isInvalid,
            errorText: props.errorText || `${props.label} is required.`
        }

        this.selectRef = props.innerRef || React.createRef();
    }

    componentDidUpdate(prevProps) {
        const { isInvalid, errorText, label } = this.props;

        if (isInvalid !== prevProps.isInvalid) {
            this.setState({ isInvalid });
        }

        if (errorText !== prevProps.errorText) {
            this.setState({ errorText });
        }

        if (label !== prevProps.label && !errorText) {
            this.setState({
                errorText: `${label} is required.`
            });
        }
    }

    handleChange = (e) => {
        const { onChange } = this.props;
        const { isInvalid } = this.state;

        // We only validate value vs. no value. Given that select can
        // not change to anything but a value, it must now be valid
        if (isInvalid) {
            this.setState({
                isInvalid: false
            });
        }

        if (onChange) {
            onChange(e);
        }
    }

    validate = () => {
        const { id, isRequired } = this.props;
        const { errorText } = this.state;

        let currentValue;
        const validationObject = {
            isInvalid: false,
            errorText: null,
            id
        };

        if (typeof this.selectRef === 'object') {
            currentValue = this.selectRef.current.value
        }
        else {
            currentValue = this.selectRef.value
        }

        if (isRequired && !currentValue) {
            validationObject.isInvalid = true;
            validationObject.errorText = errorText;

            this.setState({
                isInvalid: true
            });
        }
        else {
            this.setState({
                isInvalid: false
            });
        }

        return validationObject;
    }

    renderOptions = () => {
        const { options } = this.props;

        return options.map((item) => (
            <SelectOption key={item.value} value={item.value}>{item.label}</SelectOption>
        ))
    }

    render() {
        const {
            id,
            label,
            isLabelVisible,
            isRequired,
            isDisabled,
            isInline,
            placeholder,
            children,
            value,
            defaultValue,
            onChange,
            options,
            innerRef,
            className,
            ...rest
        } = this.props;

        const props = omit(rest, [
            'errorText',
            'isInvalid'
        ]);

        const {
            errorText,
            isInvalid
        } = this.state;

        const hasOptions = Array.isArray(options) && options.length > 0;
        const isControlled = typeof value === 'string';

        return (
            <div
                data-component="Select"
                className={cx(className, {
                    'sarsa-field-outer-container': !isInline,
                    'sarsa-field-outer-container-inline': isInline
                })}>
                <div className="sarsa-field-label-wrapper">
                    <Label
                        htmlFor={id}
                        isVisible={isLabelVisible}
                        isRequired={isRequired}>
                        {label}
                    </Label>
                </div>
                <div className="sarsa-field-inner-container">
                    {isInvalid && errorText && (
                        <div
                            id={`${id}-error`}
                            className="sarsa-field-error-message">
                            <span className="req" aria-hidden="true">*</span>
                            {errorText}
                        </div>
                    )}

                    <div className={cx('sarsa-field-input-wrapper', {
                        'inline': isInline,
                        'no-label': !isLabelVisible
                    })}>
                        <select
                            className={cx('sarsa-select', {
                                'is-invalid': isInvalid
                            })}
                            id={id}
                            defaultValue={isControlled ? undefined : defaultValue}
                            value={value}
                            onChange={this.handleChange}
                            disabled={isDisabled}
                            required={isRequired || !!placeholder}
                            aria-invalid={this.state.isInvalid}
                            aria-describedby={this.state.isInvalid && errorText ? `${id}-error` : null}
                            ref={this.selectRef}
                            {...props}>
                            {placeholder && (
                                <SelectOption value='' isDisabled>{placeholder}</SelectOption>
                            )}
                            {hasOptions && (
                                this.renderOptions()
                            )}
                            {children}
                        </select>
                    </div>
                </div>
            </div>
        );
    }
}
